import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, CreditCard, X, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PendingPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trade: {
    id: string;
    quantity: number;
    price: number;
    total_amount: number;
    created_at: string;
    ticker?: {
      symbol?: string;
      name?: string;
    };
  };
  onPaymentComplete?: () => void;
}

export function PendingPaymentModal({
  open,
  onOpenChange,
  trade,
  onPaymentComplete,
}: PendingPaymentModalProps) {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);

  // Calculate time remaining until 24-hour timeout
  const createdAt = new Date(trade.created_at).getTime();
  const timeoutAt = createdAt + 24 * 60 * 60 * 1000;
  const now = Date.now();
  const remainingMs = Math.max(0, timeoutAt - now);
  const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
  const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

  const isUrgent = remainingMs < 4 * 60 * 60 * 1000; // Less than 4 hours

  const handlePayNow = () => {
    onOpenChange(false);
    navigate("/payments");
  };

  const handleRemindLater = () => {
    onOpenChange(false);
  };

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please sign in");

      // Note: This would need a cancel-pending-trade edge function
      // For now, just close the modal and inform user
      toast.info("Please contact support to cancel a matched order", {
        description: "Orders that have been matched cannot be cancelled directly.",
      });
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const priceInDollars = trade.price / 100;
  const totalInDollars = trade.total_amount / 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
              isUrgent ? "bg-red-500/20" : "bg-amber-500/20"
            }`}
          >
            <Clock className={`h-8 w-8 ${isUrgent ? "text-red-500" : "text-amber-500"}`} />
          </div>
          <DialogTitle className="text-xl">Payment Required</DialogTitle>
          <DialogDescription>
            Your order has been matched with a seller
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Order details */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Item</span>
              <div className="flex items-center gap-2">
                {trade.ticker?.symbol && (
                  <Badge variant="outline" className="text-xs">
                    {trade.ticker.symbol}
                  </Badge>
                )}
                <span className="font-medium">{trade.ticker?.name || "SwapX Item"}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Quantity</span>
              <span className="font-medium">{trade.quantity}</span>
            </div>

            <div className="border-t pt-3 flex items-center justify-between">
              <span className="font-medium">Total Due</span>
              <span className="text-lg font-bold">${totalInDollars.toFixed(2)}</span>
            </div>
          </div>

          {/* Countdown warning */}
          <div
            className={`flex items-center gap-3 rounded-lg p-3 ${
              isUrgent ? "bg-red-500/10 border border-red-500/20" : "bg-amber-500/10 border border-amber-500/20"
            }`}
          >
            <AlertTriangle className={`h-5 w-5 ${isUrgent ? "text-red-500" : "text-amber-500"}`} />
            <div className="text-sm">
              <p className={`font-medium ${isUrgent ? "text-red-600" : "text-amber-600"}`}>
                {isUrgent ? "Time is running out!" : "Payment deadline approaching"}
              </p>
              <p className="text-muted-foreground">
                Complete payment within{" "}
                <span className="font-semibold">
                  {remainingHours}h {remainingMinutes}m
                </span>{" "}
                to avoid automatic cancellation.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handlePayNow}
            className={`w-full ${isUrgent ? "bg-red-600 hover:bg-red-700" : ""}`}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Pay Now
          </Button>
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleRemindLater} className="flex-1">
              Remind Me Later
            </Button>
            <Button
              variant="ghost"
              onClick={handleCancelOrder}
              disabled={cancelling}
              className="flex-1 text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel Order
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
